import { useEffect, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconX, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  TransformWrapper,
  TransformComponent,
  type ReactZoomPanPinchRef,
} from 'react-zoom-pan-pinch';
import { detectMediaKind, detectVideoMimeType } from '@common/media';
import ProtectedMediaWrapper from '../media/ProtectedMediaWrapper';
import useImageViewerStore from '../../store/ImageViewerStore';

/**
 * Fullscreen image/video viewer with pinch-to-zoom and responsive layout.
 *
 * Mobile (<640px): three-zone layout — top bar, image zone, navigation bar.
 *   Controls never overlap the image so respondents can read every detail
 *   of research stimuli (nutrition labels, fine print, product packaging).
 *
 * Desktop (≥640px): overlay arrows on left/right edges, close button
 *   top-right. Plenty of space so overlay controls are fine.
 *
 * Both: pinch-to-zoom (touch) and mouse-wheel zoom for reading fine text.
 *   Swipe gestures supplement the navigation buttons on touch devices.
 */
function ImageViewer() {
  const {
    isViewerOpen,
    currentImageUrl,
    currentImageAlt,
    images,
    currentIndex,
    setIsViewerOpen,
    goToNext,
    goToPrevious,
  } = useImageViewerStore();

  const handleClose = useCallback(() => setIsViewerOpen(false), [setIsViewerOpen]);
  const hasMultipleImages = images.length > 1;
  const currentItem = images[currentIndex];
  const label = currentItem?.label;
  // TELV1-565 — url, label and type must come from ONE source. The viewer used
  // to render from `currentImageUrl` while reading label from the array; any
  // caller that sets a url without a matching setImages (or a message with two
  // identical urls, where findIndex returns the first) desynced them, and once
  // type lives on the array that desync renders a video as an image: a blank
  // modal. Fall back to currentImageUrl for single-item callers.
  const activeUrl = currentItem?.url || currentImageUrl;

  // --- Keyboard navigation ---
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
      else if (e.key === 'ArrowRight' && hasMultipleImages) goToNext();
      else if (e.key === 'ArrowLeft' && hasMultipleImages) goToPrevious();
    };
    if (isViewerOpen) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isViewerOpen, handleClose, goToNext, goToPrevious, hasMultipleImages]);

  // --- Swipe gesture (touch devices) ---
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current || !hasMultipleImages || isZoomed) {
        touchStartRef.current = null;
        return;
      }
      const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
      const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
      touchStartRef.current = null;

      // Only trigger if horizontal swipe is dominant and > 50px
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (dx < 0) goToNext();
        else goToPrevious();
      }
    },
    [hasMultipleImages, isZoomed, goToNext, goToPrevious],
  );

  // --- File type detection ---
  // Prefer the type the backend already told us (chat sets it); fall back to
  // parsing the URL for callers that don't, e.g. the dashboard, which shares
  // this singleton store. The old inline sniffing split on the last dot in the
  // WHOLE url and never knew about `.mov` (TELV1-554), which rendered an empty
  // modal for QuickTime stimuli.
  const detectedKind = detectMediaKind(activeUrl);
  const isImage = currentItem?.type ? currentItem.type === 'image' : detectedKind === 'image';
  const isVideo = currentItem?.type ? currentItem.type === 'video' : detectedKind === 'video';

  // --- Counter text ---
  const counterText = hasMultipleImages ? `${currentIndex + 1} / ${images.length}` : null;

  return createPortal(
    <AnimatePresence initial={false}>
      {isViewerOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[999999] flex flex-col bg-black/90 sm:bg-black/80 sm:backdrop-blur-sm"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* ─── TOP BAR ─── */}
          <div className="flex items-center justify-between px-4 h-[52px] shrink-0 bg-black/60 sm:bg-transparent sm:absolute sm:top-0 sm:left-0 sm:right-0 sm:z-20 sm:px-6 sm:pt-5">
            <div className="flex items-center gap-2 min-w-0">
              {counterText && (
                <span className="text-white/70 text-sm font-medium whitespace-nowrap">
                  {counterText}
                </span>
              )}
              {label && (
                <span className="text-white/50 text-xs truncate max-w-[200px] sm:max-w-[400px]">
                  — {label}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="w-11 h-11 flex items-center justify-center text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors shrink-0"
              aria-label="Close"
            >
              <IconX size={22} />
            </button>
          </div>

          {/* ─── IMAGE / VIDEO ZONE ─── */}
          <div className="flex-1 min-h-0 flex items-center justify-center overflow-hidden relative">
            {/* Desktop overlay arrows (hidden on mobile) */}
            {hasMultipleImages && (
              <>
                <button
                  type="button"
                  onClick={goToPrevious}
                  className="hidden sm:flex absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 rounded-full p-2.5 backdrop-blur-sm transition-all"
                  aria-label="Previous image"
                >
                  <IconChevronLeft size={28} className="text-white" />
                </button>
                <button
                  type="button"
                  onClick={goToNext}
                  className="hidden sm:flex absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 rounded-full p-2.5 backdrop-blur-sm transition-all"
                  aria-label="Next image"
                >
                  <IconChevronRight size={28} className="text-white" />
                </button>
              </>
            )}

            {isImage && (
              <motion.div
                key={activeUrl}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="w-full h-full flex items-center justify-center"
              >
                <TransformWrapper
                  initialScale={1}
                  minScale={0.5}
                  maxScale={5}
                  centerOnInit
                  doubleClick={{ step: 1.5 }}
                  onTransform={(_ref: ReactZoomPanPinchRef, state: { scale: number }) => {
                    setIsZoomed(state.scale > 1.05);
                  }}
                  // Reset zoom when navigating to a new image
                  key={activeUrl}
                >
                  <TransformComponent
                    wrapperStyle={{ width: '100%', height: '100%' }}
                    contentStyle={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ProtectedMediaWrapper noOverlay>
                      <img
                        src={activeUrl}
                        alt={currentImageAlt}
                        // max-h-full only works when an ancestor has an
                        // explicit height. ProtectedMediaWrapper renders a
                        // div with height: auto, so the percentage chain
                        // breaks and the image grows to its intrinsic size
                        // — fine on mobile where the width constraint
                        // (max-w 100vw-2rem) tends to dominate and shrink
                        // the image enough, but on desktop a 4000×3000
                        // image at max-w 90vw on a 1920px monitor renders
                        // 1728×1296 and overflows the 1080px viewport
                        // (overflow:hidden clips the top + bottom).
                        // Switching to viewport-relative max-height on
                        // desktop guarantees the image fits without
                        // depending on the ancestor chain. TELV1-225.
                        className="max-w-[calc(100vw-2rem)] sm:max-w-[90vw] max-h-[calc(100dvh-7.5rem)] sm:max-h-[90vh] w-auto h-auto object-contain select-none"
                        draggable={false}
                      />
                    </ProtectedMediaWrapper>
                  </TransformComponent>
                </TransformWrapper>
              </motion.div>
            )}

            {isVideo && (
              <motion.div
                key={activeUrl}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="w-full h-full flex items-center justify-center p-4"
              >
                <ProtectedMediaWrapper noOverlay>
                  {/* No caption track: customer-supplied research stimuli, no
                      caption files exist for them. */}
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <video
                    controls
                    controlsList="nodownload"
                    // Same fix as the image element above — viewport-relative
                    // max-height ensures large videos don't overflow the
                    // viewer on desktop. TELV1-225.
                    className="max-w-[calc(100vw-2rem)] sm:max-w-[90vw] max-h-[calc(100dvh-7.5rem)] sm:max-h-[90vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
                  >
                    {/* `video/${ext}` produced invalid types like `video/mov`;
                        detectVideoMimeType maps mov -> video/quicktime. */}
                    <source src={activeUrl} type={detectVideoMimeType(activeUrl)} />
                    Your browser does not support the video tag.
                  </video>
                </ProtectedMediaWrapper>
              </motion.div>
            )}
          </div>

          {/* ─── MOBILE NAVIGATION BAR ─── (hidden on desktop) */}
          {hasMultipleImages && (
            <div className="flex sm:hidden items-center justify-between px-5 bg-black/60 h-[64px] shrink-0">
              <button
                type="button"
                onClick={goToPrevious}
                className="flex items-center gap-1.5 text-white text-sm font-medium h-11 px-3 rounded-lg active:bg-white/10 transition-colors"
                aria-label="Previous image"
              >
                <IconChevronLeft size={18} />
                <span>Previous</span>
              </button>
              <button
                type="button"
                onClick={goToNext}
                className="flex items-center gap-1.5 text-white text-sm font-medium h-11 px-3 rounded-lg active:bg-white/10 transition-colors"
                aria-label="Next image"
              >
                <span>Next</span>
                <IconChevronRight size={18} />
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export default ImageViewer;
